import type { PatientSearchResult, SearchMatchKind } from '@wodi/shared';
import { formatShortAge } from '@wodi/ui';

import styles from './patients.module.css';

const MATCH_WORDS: Record<SearchMatchKind, string> = {
  identifier: 'ID number',
  patient_number: 'patient number',
  phone: 'phone',
  name: 'name',
  temporary_label: 'temporary label',
};

function joinWords(words: readonly string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words.at(-1) ?? ''}`;
}

/** Short sex and age, "F, 34 y (estimated)": enough to tell similar names apart. */
export function sexAndAge(result: {
  sex: { abbreviation: string | null; display: string };
  age: PatientSearchResult['age'];
  ageEstimated: boolean;
}): string {
  return `${result.sex.abbreviation ?? result.sex.display}, ${formatShortAge(result.age, result.ageEstimated)}`;
}

/** Plain text for a result's accessible name. */
export function resultText(result: PatientSearchResult): string {
  return `${result.displayName}, ${sexAndAge(result)}, ${result.patientNumber}`;
}

/**
 * One search result (DESIGN.md §7): name, sex, age, phone last 4, patient number, what
 * matched, and the registering branch when it is not this one.
 */
export function PatientResultRow({
  result,
}: {
  result: Omit<PatientSearchResult, 'matchedOn'> & { matchedOn?: PatientSearchResult['matchedOn'] };
}) {
  return (
    <span className={styles.result}>
      <span className={styles.resultName}>{result.displayName}</span>
      <span className={`wodi-id ${styles.resultNumber}`}>{result.patientNumber}</span>
      <span className={styles.resultMeta}>
        <span>{sexAndAge(result)}</span>
        <span>{result.phoneLast4 === null ? 'No phone' : `Phone ending ${result.phoneLast4}`}</span>
        {result.matchedOn === undefined ? null : (
          <span>{`Matched on ${joinWords(result.matchedOn.map((m) => MATCH_WORDS[m]))}`}</span>
        )}
        {result.registeredHere ? null : (
          <span>{`Registered at ${result.registeredFacility.name}`}</span>
        )}
      </span>
    </span>
  );
}
